import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useLots } from '../../../../contexts/LotsContext';
import NumericKeypad from '../../../../components/NumericKeypad';

const EditLotModal = ({ lot, onClose }) => {
  const { updateLot } = useLots();
  const [formData, setFormData] = useState({
    name: lot.title || '',
    supplier: lot.supplier || '',
    unitPrice: lot.unitPrice ? lot.unitPrice.toString() : '',
    unitCost: lot.unitCost ? lot.unitCost.toString() : '',
    container_deposit: lot.container_deposit ? lot.container_deposit.toString() : '',
    notes: lot.notes || '',
    quantity: lot.quantity ? lot.quantity.toString() : '0',
  });
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeInput, setActiveInput] = useState(null);

  // Detectar si es dispositivo móvil
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFocus = (inputName) => {
    setActiveInput(inputName);
  };

  const handleBlur = () => {
    // Dejamos un pequeño delay para que el teclado se pueda usar
    setTimeout(() => {
      if (document.activeElement.tagName !== 'INPUT') {
        setActiveInput(null);
      }
    }, 100);
  };

  const handleNumericInput = (value) => {
    if (!activeInput) return;
    
    setFormData(prev => {
      const currentValue = prev[activeInput] || '';
      return {
        ...prev,
        [activeInput]: currentValue + value
      };
    });
  };

  const handleNumericDelete = () => {
    if (!activeInput) return;
    
    setFormData(prev => {
      const currentValue = prev[activeInput] || '';
      return {
        ...prev,
        [activeInput]: currentValue.slice(0, -1)
      };
    });
  };

  const handleNumericConfirm = () => {
    setActiveInput(null);
  };

  const handleNext = () => {
    if (!activeInput) return;
    
    const inputs = ['name', 'supplier', 'unitPrice', 'unitCost', 'container_deposit', 'quantity', 'notes'];
    const currentIndex = inputs.indexOf(activeInput);
    
    if (currentIndex < inputs.length - 1) {
      const nextInput = inputs[currentIndex + 1];
      document.querySelector(`[name="${nextInput}"]`)?.focus();
    } else {
      // Si es el último, confirmar
      handleNumericConfirm();
    }
  };

  const hasNextInput = () => {
    if (!activeInput) return false;
    
    const inputs = ['name', 'supplier', 'unitPrice', 'unitCost', 'container_deposit', 'quantity', 'notes'];
    const currentIndex = inputs.indexOf(activeInput);
    
    return currentIndex < inputs.length - 1;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError(null);
      setIsLoading(true);
      
      // Preparar los datos para actualizar
      const lotData = {
        ...lot,
        title: formData.name,
        supplier: formData.supplier || null,
        unitPrice: formData.unitPrice ? parseFloat(formData.unitPrice) : null,
        unitCost: formData.unitCost ? parseFloat(formData.unitCost) : null,
        container_deposit: formData.container_deposit ? parseFloat(formData.container_deposit) : null,
        notes: formData.notes || null,
        quantity: formData.quantity ? parseFloat(formData.quantity) : 0,
      };

      // Guardar cambios
      await updateLot(lot.id, lotData);
      
      // Cerrar el modal
      onClose(true); // Pasamos true para indicar que se realizaron cambios
    } catch (err) {
      setError(err.message || 'Error al actualizar el lote');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={() => onClose(false)}
      />

      {/* Modal */}
      <div className="fixed inset-x-0 bottom-0 bg-white dark:bg-gray-800 rounded-t-xl p-4 z-50 space-y-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Editar Lote
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {lot.display_id || lot.id} - {lot.title}
            </p>
          </div>
          <button 
            onClick={() => onClose(false)}
            className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nombre del Lote *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onFocus={() => handleFocus('name')}
              onBlur={handleBlur}
              className="w-full p-3 border dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500"
              placeholder="Ej. Bananas Estela"
              required
            />
          </div>

          <div>
            <label htmlFor="supplier" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Proveedor
            </label>
            <input
              type="text"
              id="supplier"
              name="supplier"
              value={formData.supplier}
              onChange={handleChange}
              onFocus={() => handleFocus('supplier')}
              onBlur={handleBlur}
              className="w-full p-3 border dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500"
              placeholder="Ej. Juan Pérez"
            />
          </div>

          {/* Información del lote - Solo mostramos cantidad inicial, permitimos editar cantidad disponible */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Cantidad Inicial
              </label>
              <div className="w-full p-3 border dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700">
                {lot.initialQuantity} {lot.unit}
              </div>
            </div>
            <div>
              <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Cantidad Disponible
              </label>
              <input
                type="text"
                inputMode="numeric"
                id="quantity"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                onFocus={() => handleFocus('quantity')}
                onBlur={handleBlur}
                className="w-full p-3 border dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500 text-center"
                placeholder="0"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="unitPrice" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Precio Sugerido
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <span className="text-gray-500 dark:text-gray-400">$</span>
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  id="unitPrice"
                  name="unitPrice"
                  value={formData.unitPrice}
                  onChange={handleChange}
                  onFocus={() => handleFocus('unitPrice')}
                  onBlur={handleBlur}
                  className="w-full p-3 pl-8 border dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500"
                  placeholder="0"
                />
              </div>
            </div>
            <div>
              <label htmlFor="unitCost" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Costo Unitario
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <span className="text-gray-500 dark:text-gray-400">$</span>
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  id="unitCost"
                  name="unitCost"
                  value={formData.unitCost}
                  onChange={handleChange}
                  onFocus={() => handleFocus('unitCost')}
                  onBlur={handleBlur}
                  className="w-full p-3 pl-8 border dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500"
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="container_deposit" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Depósito Envases
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span className="text-gray-500 dark:text-gray-400">$</span>
              </div>
              <input
                type="text"
                inputMode="numeric"
                id="container_deposit"
                name="container_deposit"
                value={formData.container_deposit}
                onChange={handleChange}
                onFocus={() => handleFocus('container_deposit')}
                onBlur={handleBlur}
                className="w-full p-3 pl-8 border dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500"
                placeholder="0"
              />
            </div>
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notas
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              onFocus={() => handleFocus('notes')}
              onBlur={handleBlur}
              className="w-full p-3 border dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500"
              placeholder="Notas adicionales sobre el lote..."
              rows={3}
            />
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="flex-1 py-3 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.name}
              className="flex-1 py-3 bg-blue-500 dark:bg-blue-600 text-white rounded-lg disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:text-gray-500 dark:disabled:text-gray-400 hover:bg-blue-600 dark:hover:bg-blue-700 transition-colors"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Guardando...
                </div>
              ) : (
                'Guardar cambios'
              )}
            </button>
          </div>
        </form>

        {/* Teclado numérico - Solo visible en dispositivos móviles cuando hay un input activo */}
        {isMobile && activeInput && (
          <div className="fixed inset-x-0 bottom-0 z-50">
            <NumericKeypad
              onInput={handleNumericInput}
              onDelete={handleNumericDelete}
              onConfirm={handleNumericConfirm}
              onNext={handleNext}
              hasNext={hasNextInput()}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default EditLotModal; 